import { FC } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import QRExample from '~/assets/icons/api_param_ex.png';
import ContentContainer from '~/components/atoms/content-container/ContentContainer';
import { COLORS } from '~/styles/colors';

interface ParamRow {
  parameter: string;
  type: string;
  default: string;
  description: string;
}

interface ImageItem {
  img: string;
  title: string;
}

interface ExtendedRow {
  head: string;
  type: string;
  default: string;
  description: string;
  images?: ImageItem[];
}

interface TableProps {
  title: string;
}

const mainParameters: ParamRow[] = [
  { parameter: 'apikey', type: 'String', default: 'Essential', description: 'Get it from your account settings.' },
  {
    parameter: 'data',
    type: 'String',
    default: 'Essential',
    description: 'What you like to encode (e.g., URL or Plain Text).',
  },
  { parameter: 'title', type: 'String', default: 'Optional', description: 'TextField / Name of your QR Code.' },
  {
    parameter: 'folderid',
    type: 'Number',
    default: 'Optional',
    description: 'Organize your QR Codes inside your folders.',
  },
];

const colorShapeParameters: ExtendedRow[] = [
  {
    head: 'Cell',
    type: 'String',
    default: 'off',
    description:
      "Set 'on' if you want background to be transparent. Otherwise, set 'off' and choose background color below.",
  },
  { head: 'backcolor', type: 'String', default: '#ffffff', description: 'Background color of your QR Code.' },
  { head: 'frontcolor', type: 'String', default: '#000000', description: 'Color of the QR Code dots.' },
  {
    head: 'gradient',
    type: 'String',
    default: 'off',
    description: "Set 'on' if you want the color of the dots to be a gradient. Otherwise, set 'off'.",
  },
  {
    head: 'radial',
    type: 'String',
    default: 'off',
    description: "Set 'on' if you want the color of the dots to be a radial gradient. Otherwise, set 'off'.",
  },
  {
    head: 'gradient_color',
    type: 'String',
    default: '#000000',
    description: "Choose a color if you set 'yes' on gradient parameter.",
  },
  { head: 'marker_out_color', type: 'String', default: '#000000', description: 'Color of the outside-corners marker.' },
  { head: 'marker_in_color', type: 'String', default: '#000000', description: 'Color of the inside-corners marker.' },
  {
    head: 'pattern',
    type: 'String',
    default: 'default',
    description: 'Pattern of the QR code dots.',
    images: [
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
    ],
  },
  {
    head: 'marker',
    type: 'String',
    default: 'default',
    description: 'Shape of the markers.',
    images: [
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
    ],
  },
  {
    head: 'marker_in',
    type: 'String',
    default: 'default',
    description: 'Shape of the inner markers.',
    images: [
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
    ],
  },
  {
    head: 'no_logo_bg',
    type: 'String',
    default: 'off',
    description: "Set 'on' if you want no background behind your logo. Otherwise, set 'off'.",
  },
];

const frameParameters: ExtendedRow[] = [
  {
    head: 'outer_frame',
    type: 'String',
    default: 'none',
    description: 'The outer frame of the QR code.',
    images: [
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
      { img: QRExample, title: 'default' },
    ],
  },
  { head: 'framelabel', type: 'String', default: 'SCAN ME', description: 'The text you want the frame to say.' },
  {
    head: 'label_font',
    type: 'String',
    default: 'Arial, Helvetica, sans-serif',
    description:
      'Font of framelabel. Choose between: Arial, Helvetica, sans-serif - "Times New Roman", Times, serif - \'Courier New\', Courier, monospace.',
  },
  {
    head: 'custom_frame_color',
    type: 'String',
    default: 'off',
    description: "Set 'on' if you want no background behind your logo. Otherwise, set 'off'.",
  },
  { head: 'framecolor', type: 'String', default: '#000000', description: 'Background color of your frame.' },
];

export const TableMainParams: FC<TableProps> = ({ title }) => {
  return (
    <ContentContainer
      display="flex"
      justifyContent="space-between"
      title={title}
      resetPadding
      containerStyles={{ mb: 3, p: 2 }}
    >
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow sx={{ backgroundColor: COLORS.purpleBG }}>
              <TableCell>Parameter</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mainParameters.map((row) => (
              <TableRow key={row.parameter}>
                <TableCell sx={{ backgroundColor: COLORS.purpleBG }}>{row.parameter}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.default}</TableCell>
                <TableCell>{row.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ContentContainer>
  );
};

export const ColorShapeTable: React.FC<TableProps> = ({ title }) => {
  return (
    <ContentContainer
      display="flex"
      justifyContent="space-between"
      title={title}
      resetPadding
      containerStyles={{ mb: 3, p: 2 }}
    >
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="color shape table">
          <TableHead>
            <TableRow sx={{ backgroundColor: COLORS.purpleBG }}>
              <TableCell>Head</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {colorShapeParameters.map((row) => (
              <TableRow key={row.head}>
                <TableCell sx={{ backgroundColor: COLORS.purpleBG }}>{row.head}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.default}</TableCell>
                <TableCell sx={{ display: 'flex', flexDirection: 'column' }}>
                  {row.images
                    ? row.images.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                          <img
                            src={item.img}
                            alt={row.head}
                            style={{ maxHeight: 50, maxWidth: 50, marginRight: 10, padding: 6 }}
                          />
                          <Typography>{item.title}</Typography>
                        </Box>
                      ))
                    : row.description}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ContentContainer>
  );
};

export const FrameTable: React.FC<TableProps> = ({ title }) => {
  return (
    <ContentContainer
      display="flex"
      justifyContent="space-between"
      title={title}
      resetPadding
      containerStyles={{ mb: 3, p: 2 }}
    >
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="frame table">
          <TableHead>
            <TableRow sx={{ backgroundColor: COLORS.purpleBG }}>
              <TableCell>Head</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {frameParameters.map((row) => (
              <TableRow key={row.head}>
                <TableCell sx={{ backgroundColor: COLORS.purpleBG }}>{row.head}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.default}</TableCell>
                <TableCell>
                  {row.images
                    ? row.images.map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                          <img
                            src={item.img}
                            alt={row.head}
                            style={{ maxHeight: 50, maxWidth: 50, marginRight: 10, padding: 6 }}
                          />
                          <Typography>{item.title}</Typography>
                        </Box>
                      ))
                    : row.description}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ContentContainer>
  );
};
